import {
  memo,
  type FC,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react"
import { useTranslation } from "react-i18next"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

import type { DragMode } from "helpers/utils/spanRibbonWindow"
import {
  HANDLE_GRIP_WIDTH,
  SCRUBBER_HEIGHT,
  SCRUBBER_LABEL_HEIGHT,
  SCRUBBER_MASK_COLOR,
  SCRUBBER_TOP_GAP,
} from "./helpers"

interface WindowScrubberProps {
  // Spans on the line, and the window over them, in span units.
  total: number
  start: number
  size: number
  isFitted: boolean
  // Aligns the strip with the lanes, which start after the method gutter.
  labelWidth: number
  handleHitWidth: number
  // Whether the touch overrides are in play, which changes the preamble.
  isCompact: boolean
  // The whole line as one gradient, built by the ribbon so a drag never
  // rebuilds it.
  overviewGradient: string
  // The window outline and grips, in the palette's hover colour.
  frameColor: string
  // The track the drag measures against. Owned by the ribbon, which reads it
  // on pointer down.
  trackRef: RefObject<HTMLDivElement>
  beginDrag: (mode: DragMode | "track") => (event: ReactPointerEvent<HTMLElement>) => void
  dragHandlers: {
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void
    onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void
  }
  onFitAll: () => void
}

/**
 * The whole line at one screen width whatever the span count, with the window
 * drawn over it. This replaced horizontal scrolling, so the line never leaves
 * the card.
 */
const WindowScrubber: FC<WindowScrubberProps> = ({
  total,
  start,
  size,
  isFitted,
  labelWidth,
  handleHitWidth,
  isCompact,
  overviewGradient,
  frameColor,
  trackRef,
  beginDrag,
  dragHandlers,
  onFitAll,
}) => {
  const { t } = useTranslation()

  const startPct = (start / total) * 100
  const sizePct = (size / total) * 100

  return (
    <Box sx={{ mt: `${SCRUBBER_TOP_GAP}px` }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ height: SCRUBBER_LABEL_HEIGHT, ml: `${labelWidth}px` }}
      >
        {/* Narrow layouts drop the "Whole line" preamble rather than cut the
            range, which is the half that changes. */}
        <Typography
          sx={{
            minWidth: 0,
            fontSize: "0.65rem",
            color: "text.secondary",
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t(
            isCompact
              ? "spans.ampacity.window.spanCount"
              : "spans.ampacity.window.wholeLine",
            { total },
          )}
          {" · "}
          {t("spans.ampacity.window.showing", { from: start + 1, to: start + size })}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          color="primary"
          onClick={onFitAll}
          disabled={isFitted}
          sx={{ flexShrink: 0, minWidth: 0, py: 0.25, px: 1.5, fontSize: "0.7rem" }}
        >
          {t("spans.ampacity.window.fitAll")}
        </Button>
      </Stack>

      <Box
        ref={trackRef}
        role="slider"
        aria-label={String(t("spans.ampacity.window.rangeLabel"))}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={start + 1}
        onPointerDown={beginDrag("track")}
        {...dragHandlers}
        sx={{
          position: "relative",
          height: SCRUBBER_HEIGHT,
          ml: `${labelWidth}px`,
          borderRadius: "3px",
          cursor: "pointer",
          // The same recessed bed as the app's scrollbar, since this strip took
          // its place. The shadow is on the track, so it paints under every
          // child and leaves the heat colors alone.
          bgcolor: "grey.900",
          boxShadow: (theme) => `inset 0 0 4px 1px ${theme.palette.neutral[100]}`,
          // Not clipped: the handles straddle the window edges, so at either end
          // half of one hangs past the track and clipping would cut it, leaving
          // the window looking open-ended.
          touchAction: "pan-y",
        }}
      >
        {/* One gradient rather than a box per span: neighbours blend, so 250
            read as heat and not a picket fence, and a drag repaints one node
            instead of a thousand. */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "3px",
            backgroundImage: overviewGradient,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: `${startPct}%`,
            bgcolor: SCRUBBER_MASK_COLOR,
            borderRadius: "3px 0 0 3px",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            width: `${100 - startPct - sizePct}%`,
            bgcolor: SCRUBBER_MASK_COLOR,
            borderRadius: "0 3px 3px 0",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${startPct}%`,
            width: `${sizePct}%`,
            border: `1px solid ${frameColor}`,
            borderRadius: "3px",
            pointerEvents: "none",
          }}
        />
        {(["left", "right"] as const).map((side) => (
          <Box
            key={side}
            onPointerDown={beginDrag(side)}
            {...dragHandlers}
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `calc(${side === "left" ? startPct : startPct + sizePct}% - ${
                handleHitWidth / 2
              }px)`,
              width: handleHitWidth,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "ew-resize",
              touchAction: "none",
            }}
          >
            <Box
              sx={{
                width: HANDLE_GRIP_WIDTH,
                height: "62%",
                borderRadius: "2px",
                bgcolor: frameColor,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// Nothing here depends on the hover, and every other prop is stable, so the
// scrubber sits out the render the ribbon does once per cell the pointer
// crosses.
export default memo(WindowScrubber)
